import { useState } from "react"
import { Button } from "@ui/Button"
import { Dialog } from "@ui/Dialog"
import { Input } from "@ui/Input"
import { formatDuration, formatTimeOnly } from "./formatters"
import SearchIcon from "../../icons/cisco-momentum/core/search-regular.svg?react"

function computeDuration(joinedAt, leftAt) {
  if (!joinedAt || !leftAt) return null
  return Math.round((new Date(leftAt) - new Date(joinedAt)) / 1000)
}

export default function AttendanceModal({ isOpen, onClose, loading, data, error }) {
  const [ search, setSearch ] = useState("")
  const [ sortBy, setSortBy ] = useState("name")
  const [ sortDir, setSortDir ] = useState("asc")

  function toggleSort(field) {
    if (sortBy === field) {
      setSortDir(prev => prev === "asc" ? "desc" : "asc")
    } else {
      setSortBy(field)
      setSortDir("asc")
    }
  }

  function getFiltered() {
    let list = data || []
    if (search) {
      const q = search.toLowerCase()
      list = list.filter(p => (p.name || "").toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q))
    }
    const dir = sortDir === "asc" ? 1 : -1
    list = [ ...list ].sort((a, b) => {
      if (sortBy === "name") return (a.name || "").localeCompare(b.name || "") * dir
      if (sortBy === "joined") return ((a.joined_at || "") > (b.joined_at || "") ? 1 : -1) * dir
      if (sortBy === "left") return ((a.left_at || "") > (b.left_at || "") ? 1 : -1) * dir
      if (sortBy === "duration") return ((a.duration_seconds || 0) - (b.duration_seconds || 0)) * dir
      return 0
    })
    return list
  }

  const filtered = getFiltered()
  const columns = [
    { key: "name", label: "Name" },
    { key: "joined", label: "Joined" },
    { key: "left", label: "Left" },
    { key: "duration", label: "Duration" }
  ]

  function handleDownload() {
    const rows = [[ "Name", "Email", "Joined", "Left", "Duration (seconds)" ]]
    data.forEach(p => {
      rows.push([ p.name || "", p.email || "", p.joined_at || "", p.left_at || "", p.duration_seconds ?? "" ])
    })
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n")
    const blob = new Blob([ csv ], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "attendance.csv"
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={(o) => { if (!o) onClose() }}>
      <Dialog.Content size="lg">
        <Dialog.Header>
          <Dialog.Title>Attendance</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body>
          {loading ? (
            <div className="py-8 text-center text-sm text-webex-gray-600 dark:text-webex-gray-400">Loading attendance...</div>
          ) : error ? (
            <div className="py-4 text-center text-sm text-webex-red-700 dark:text-webex-red-400">{error}</div>
          ) : (data || []).length === 0 ? (
            <div className="py-8 text-center text-sm text-webex-gray-600 dark:text-webex-gray-400">No attendance data available.</div>
          ) : (
            <div className="space-y-3">
              <Input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                leadingIcon={<SearchIcon aria-hidden="true" />}
              />
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-webex-gray-200 dark:border-webex-gray-800 text-left">
                      {columns.map(col => (
                        <th
                          key={col.key}
                          onClick={() => toggleSort(col.key)}
                          className="py-2 px-2 font-medium text-webex-gray-800 dark:text-webex-gray-200 cursor-pointer select-none hover:text-webex-gray-1000 dark:hover:text-white"
                        >
                          {col.label}
                          {sortBy === col.key && (
                            <span className="ml-1 text-webex-gray-500 dark:text-webex-gray-400">{sortDir === "asc" ? "↑" : "↓"}</span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-webex-gray-100 dark:divide-webex-gray-850">
                    {filtered.map((p, i) => (
                      <tr key={i}>
                        <td className="py-2.5 px-2">
                          <p className="font-medium text-webex-gray-1000 dark:text-white">{p.name || "Unknown"}</p>
                          {p.email && <p className="text-xs text-webex-gray-600 dark:text-webex-gray-400">{p.email}</p>}
                        </td>
                        <td className="py-2.5 px-2 text-webex-gray-700 dark:text-webex-gray-300">{formatTimeOnly(p.joined_at)}</td>
                        <td className="py-2.5 px-2 text-webex-gray-700 dark:text-webex-gray-300">{formatTimeOnly(p.left_at)}</td>
                        <td className="py-2.5 px-2 text-webex-gray-700 dark:text-webex-gray-300">{formatDuration(p.duration_seconds ?? computeDuration(p.joined_at, p.left_at))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filtered.length === 0 && (
                  <p className="text-center text-sm text-webex-gray-600 dark:text-webex-gray-400 py-4">No matching participants.</p>
                )}
              </div>
              <p className="text-xs text-webex-gray-500 dark:text-webex-gray-400">{data.length} participant{data.length !== 1 ? "s" : ""} total</p>
            </div>
          )}
        </Dialog.Body>
        <Dialog.Footer>
          <div className="flex items-center gap-2 w-full">
            <Button variant="secondary" onClick={onClose}>Close</Button>
            <div className="flex-1" />
            {(data || []).length > 0 && (
              <Button variant="primary" onClick={handleDownload}>
                Download attendance report
              </Button>
            )}
          </div>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}
